import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useI18n } from '../../../i18n';
import { useAccesosMap } from '../../../hooks/useModuloAcceso';
import { ALL_MODULES } from '../../../config/modules';

// ═══════════════════════════════════════════════════════════════
// UserMenu — prompt 0520
// Avatar circular + dropdown con info del usuario y acciones.
// Sustituye los botones sueltos del header (Backoffice, Cambiar
// organización, badge SUPERADMIN literal y logout).
// ═══════════════════════════════════════════════════════════════

function getInitials(nombreCompleto: string | null | undefined, email: string): string {
  if (nombreCompleto && nombreCompleto.trim().length > 0) {
    const parts = nombreCompleto.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export function UserMenu() {
  const { user, isPlatformAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // 0615e: accesos especiales por modulo (ADR-024) - solo MEMBER tiene entradas.
  // Los demas roles tienen niveles implicitos: admin -> ADMIN, auditor -> VIEWER.
  const { accesosMap } = useAccesosMap();
  const accesosEspeciales = Object.entries(accesosMap)
    .filter(([, nivel]) => nivel === 'EDITOR' || nivel === 'ADMIN')
    .map(([modulo, nivel]) => {
      const meta = ALL_MODULES.find((m) => m.code === modulo);
      return { modulo, nivel, name: meta?.name ?? modulo };
    })
    .sort((a, b) => a.modulo.localeCompare(b.modulo, undefined, { numeric: true }));

  // Click fuera cierra el menú
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const m = t.sidebar.userMenu;
  const initials = getInitials(user.nombreCompleto, user.email);
  const displayName = user.nombreCompleto?.trim() || user.email.split('@')[0];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={m.miCuenta}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 8px 4px 4px',
          background: open ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 24,
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: 'rgba(255,255,255,0.9)',
          transition: 'background 0.15s',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            // 0617: paleta corporativa V·IAnet - celeste #33BCE1 (frontend.md)
            background: '#33BCE1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: 0.5,
          }}
        >
          {initials}
        </div>
        <span style={{ fontSize: 12, fontWeight: 500 }}>{displayName}</span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 260,
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            zIndex: 1000,
            fontFamily: 'inherit',
            overflow: 'hidden',
          }}
        >
          {/* Cabecera con info del usuario */}
          <div
            style={{
              padding: '14px 16px 12px',
              borderBottom: '1px solid #F3F4F6',
              background: '#FAFAFA',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
              {displayName}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>{user.email}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: '#EEF2FF',
                  color: '#4338CA',
                }}
              >
                {m.rolBadge}: {user.role}
              </span>
              {isPlatformAdmin && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 600,
                    background: '#F5F3FF',
                    color: '#7C3AED',
                  }}
                >
                  ★ {m.superadminBadge}
                </span>
              )}
            </div>

            {/* 0615e: accesos especiales por modulo (ADR-024).
                MEMBER + EDITOR/ADMIN en algun modulo -> lista. Admins implicitos
                (SUPERADMIN/OWNER/TENANT_ADMIN) muestran nota "Admin de todos
                los modulos". AUDITOR muestra "Solo lectura". */}
            {user.role === 'MEMBER' && accesosEspeciales.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: 4,
                  }}
                >
                  Accesos especiales
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {accesosEspeciales.map((a) => (
                    <div
                      key={a.modulo}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: 10,
                          color: '#111827',
                          minWidth: 28,
                        }}
                      >
                        {a.modulo}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: a.nivel === 'ADMIN' ? '#2F5496' : '#33BCE1',
                          color: '#fff',
                          letterSpacing: '0.4px',
                        }}
                      >
                        {a.nivel}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: '#6B7280',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {a.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {user.role === 'MEMBER' && accesosEspeciales.length === 0 && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: '#9CA3AF',
                  fontStyle: 'italic',
                }}
              >
                Sin accesos especiales (solo lectura por defecto)
              </div>
            )}
            {(user.role === 'SUPERADMIN_PLATFORM' ||
              user.role === 'TENANT_OWNER' ||
              user.role === 'TENANT_ADMIN') && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: '#15803D',
                  fontWeight: 600,
                }}
              >
                ✓ Admin implicito de todos los modulos
              </div>
            )}
            {user.role === 'TENANT_AUDITOR' && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: '#6B7280',
                  fontStyle: 'italic',
                }}
              >
                Solo lectura en todos los modulos
              </div>
            )}
          </div>

          {/* Items */}
          {isPlatformAdmin && (
            <MenuItem
              label={m.backoffice}
              onClick={() => {
                setOpen(false);
                navigate('/admin/backoffice');
              }}
            />
          )}
          <MenuItem
            label={m.cambiarOrganizacion}
            onClick={() => {
              setOpen(false);
              navigate('/tenant-selector');
            }}
          />
          {user.provider !== 'dev' && (
            <MenuItem
              label={m.cerrarSesion}
              danger
              onClick={() => {
                setOpen(false);
                logout();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 16px',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        fontSize: 13,
        color: danger ? '#DC2626' : '#374151',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: danger ? 600 : 500,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = danger ? '#FEF2F2' : '#F3F4F6';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {label}
    </button>
  );
}
